import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import { toManilaDayBoundaryIso } from '@/lib/date-range';
import type { components, paths } from '@/lib/api/generated';
import { formatReceiptAmount } from '@/lib/receipt-formatters';

export const DASHBOARD_SUMMARY_QUERY_KEY_PREFIX = ['dashboard', 'merchant-summary'] as const;
export const DASHBOARD_RECENT_RECEIPTS_QUERY_KEY_PREFIX = ['dashboard', 'recent-receipts'] as const;
export const DASHBOARD_DEVICES_HEALTH_QUERY_KEY_PREFIX = [
  'dashboard',
  'merchant-devices-health',
] as const;
export const ANALYTICS_PIPELINE_QUERY_KEY_PREFIX = ['analytics', 'pipeline'] as const;
export const ANALYTICS_DEVICES_HEALTH_QUERY_KEY_PREFIX = ['analytics', 'devices-health'] as const;
export const RECEIPTS_QUERY_KEY_PREFIX = ['merchant', 'receipts'] as const;
export const RECEIPT_DETAIL_QUERY_KEY_PREFIX = ['merchant', 'receipt-detail'] as const;
export const RECEIPT_CREATED_TOAST_DURATION_MS = 4_000;

// Merchant notification-center caches. Defined here (not in the feature) so the
// SSE `notification.new` handler and the feature's query-options share one
// source of truth; the feature imports these back.
export const NOTIFICATIONS_LIST_QUERY_KEY_PREFIX = ['merchant', 'notifications', 'list'] as const;
export const NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY = [
  'merchant',
  'notifications',
  'unread-count',
] as const;
export const NOTIFICATION_CREATED_TOAST_DURATION_MS = 6_000;

const MAX_RECONNECT_DELAY_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MANILA_TIME_ZONE = 'Asia/Manila';

export type MerchantStreamStatus = 'connected' | 'reconnecting';

type MerchantReceiptListOperation = paths['/api/v1/merchant/receipts']['get'];

type MerchantReceiptsResponse =
  MerchantReceiptListOperation['responses']['200']['content']['application/json'];

type MerchantReceiptItem = MerchantReceiptsResponse['items'][number];

type MerchantSummaryResponse =
  paths['/api/v1/merchant/summary']['get']['responses']['200']['content']['application/json'];

type MerchantSummaryPeriod =
  NonNullable<paths['/api/v1/merchant/summary']['get']['parameters']['query']>['period'];

type MerchantReceiptPeriod = components['schemas']['MerchantReceiptPeriod'];
type MerchantReceiptStatus = components['schemas']['ReceiptStatus'];

interface ReceiptsQueryKeyMeta {
  q: string | null;
  status: MerchantReceiptStatus | null;
  hasImage: boolean | null;
  period: MerchantReceiptPeriod | null;
  from: string | null;
  to: string | null;
  branchId: string | null;
  page: number;
  pageSize: number;
}

interface DashboardRecentReceiptsQueryKeyMeta {
  period: MerchantSummaryPeriod | null;
  from: string | null;
  to: string | null;
  branchId: string | null;
  page: number;
  pageSize: number;
}

interface DashboardSummaryQueryKeyMeta {
  period: MerchantSummaryPeriod;
  from: string | null;
  to: string | null;
  branchId: string | null;
}

export interface ReceiptCreatedEventPayload {
  receipt_id?: string;
  merchant_id?: string;
  branch_id: string;
  transaction_id: string;
  total_centavos: number | string | null;
  currency?: string;
  captured_at: string;
  status?: MerchantReceiptStatus;
  has_image?: boolean;
}

export interface NormalizedReceiptCreatedEvent {
  branchId: string;
  transactionId: string;
  capturedAt: string;
  totalCentavos: string | null;
  totalCentavosNumber: number | null;
  status: MerchantReceiptStatus;
  hasImage: boolean;
}

const manilaDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: MANILA_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function getMerchantStreamRetryDelay(attempt: number): number {
  if (!Number.isFinite(attempt) || attempt <= 0) {
    return INITIAL_RECONNECT_DELAY_MS;
  }

  const delay = INITIAL_RECONNECT_DELAY_MS * 2 ** (attempt - 1);
  return Math.min(delay, MAX_RECONNECT_DELAY_MS);
}

export function parseReceiptCreatedEventPayload(data: string): NormalizedReceiptCreatedEvent | null {
  let parsed: unknown;

  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const payload = parsed as Partial<ReceiptCreatedEventPayload>;
  const branchId = payload.branch_id?.trim();
  const transactionId = payload.transaction_id?.trim();

  if (!branchId || !transactionId) {
    return null;
  }

  const capturedAt = payload.captured_at;
  if (!capturedAt || Number.isNaN(Date.parse(capturedAt))) {
    return null;
  }

  const totalCentavos = normalizeTotalCentavos(payload.total_centavos);
  const totalCentavosNumber = totalCentavos === null ? null : Number(totalCentavos);

  return {
    branchId,
    transactionId,
    capturedAt,
    totalCentavos,
    totalCentavosNumber: Number.isFinite(totalCentavosNumber) ? totalCentavosNumber : null,
    status: payload.status ?? 'pending',
    hasImage: payload.has_image ?? false,
  };
}

export function shouldToastReceiptCreatedEvent(visibilityState: DocumentVisibilityState): boolean {
  return visibilityState === 'visible';
}

export function getReceiptCreatedToastMessage(args: {
  payload: NormalizedReceiptCreatedEvent;
  branchNameById: ReadonlyMap<string, string>;
}): string {
  const { payload, branchNameById } = args;
  const segments = ['New receipt'];
  const amount = formatReceiptAmount(payload.totalCentavos);

  if (amount !== '—') {
    segments.push(amount);
  }

  const branchName = branchNameById.get(payload.branchId)?.trim();
  if (branchName) {
    segments.push(branchName);
  }

  return segments.join(' - ');
}

export function patchReceiptCreatedEventInCache(args: {
  queryClient: QueryClient;
  payload: NormalizedReceiptCreatedEvent;
  branchNameById: ReadonlyMap<string, string>;
}) {
  const { queryClient, payload, branchNameById } = args;
  const candidateRow = toCandidateReceiptRow(payload, branchNameById.get(payload.branchId));

  const receiptsQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: RECEIPTS_QUERY_KEY_PREFIX });

  for (const query of receiptsQueries) {
    queryClient.setQueryData<MerchantReceiptsResponse>(query.queryKey, (current) => {
      if (!current) return current;

      return applyReceiptCreatedEventToReceiptList({
        current,
        queryKey: query.queryKey,
        payload,
        candidateRow,
      });
    });
  }

  const dashboardRecentQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: DASHBOARD_RECENT_RECEIPTS_QUERY_KEY_PREFIX });

  for (const query of dashboardRecentQueries) {
    queryClient.setQueryData<MerchantReceiptsResponse>(query.queryKey, (current) => {
      if (!current) return current;

      return applyReceiptCreatedEventToDashboardRecentReceipts({
        current,
        queryKey: query.queryKey,
        payload,
        candidateRow,
      });
    });
  }

  const dashboardSummaryQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: DASHBOARD_SUMMARY_QUERY_KEY_PREFIX });

  for (const query of dashboardSummaryQueries) {
    queryClient.setQueryData<MerchantSummaryResponse>(query.queryKey, (current) => {
      if (!current) return current;

      return applyReceiptCreatedEventToDashboardSummary({
        current,
        queryKey: query.queryKey,
        payload,
      });
    });
  }
}

export function refetchActiveMerchantStreamFallbackQueries(queryClient: QueryClient) {
  void queryClient.refetchQueries({
    queryKey: DASHBOARD_SUMMARY_QUERY_KEY_PREFIX,
    type: 'active',
  });

  void queryClient.refetchQueries({
    queryKey: DASHBOARD_RECENT_RECEIPTS_QUERY_KEY_PREFIX,
    type: 'active',
  });

  void queryClient.refetchQueries({
    queryKey: DASHBOARD_DEVICES_HEALTH_QUERY_KEY_PREFIX,
    type: 'active',
  });

  void queryClient.refetchQueries({
    queryKey: ANALYTICS_PIPELINE_QUERY_KEY_PREFIX,
    type: 'active',
  });

  void queryClient.refetchQueries({
    queryKey: ANALYTICS_DEVICES_HEALTH_QUERY_KEY_PREFIX,
    type: 'active',
  });

  void queryClient.refetchQueries({
    queryKey: RECEIPTS_QUERY_KEY_PREFIX,
    type: 'active',
  });

  void queryClient.refetchQueries({
    queryKey: RECEIPT_DETAIL_QUERY_KEY_PREFIX,
    type: 'active',
  });
}

export function applyReceiptCreatedEventToReceiptList(args: {
  current: MerchantReceiptsResponse;
  queryKey: QueryKey;
  payload: NormalizedReceiptCreatedEvent;
  candidateRow: MerchantReceiptItem;
}): MerchantReceiptsResponse {
  const { current, queryKey, payload, candidateRow } = args;
  const queryMeta = parseReceiptsQueryKey(queryKey);

  if (!queryMeta) {
    return current;
  }

  const nextItems = [...current.items];
  const existingIndex = nextItems.findIndex((item) => item.transaction_id === payload.transactionId);

  if (existingIndex >= 0) {
    const existingItem = nextItems[existingIndex];
    if (!existingItem) {
      return current;
    }

    nextItems[existingIndex] = mergeReceiptRow(existingItem, payload, candidateRow.branch_name);

    return {
      ...current,
      items: nextItems,
    };
  }

  if (!matchesReceiptFilters(payload, queryMeta)) {
    return current;
  }

  const nextTotal = current.total + 1;

  if (queryMeta.page !== 1) {
    return {
      ...current,
      total: nextTotal,
    };
  }

  nextItems.unshift(candidateRow);
  if (nextItems.length > queryMeta.pageSize) {
    nextItems.pop();
  }

  return {
    ...current,
    total: nextTotal,
    items: nextItems,
  };
}

export function applyReceiptCreatedEventToDashboardRecentReceipts(args: {
  current: MerchantReceiptsResponse;
  queryKey: QueryKey;
  payload: NormalizedReceiptCreatedEvent;
  candidateRow: MerchantReceiptItem;
}): MerchantReceiptsResponse {
  const { current, queryKey, payload, candidateRow } = args;
  const queryMeta = parseDashboardRecentReceiptsQueryKey(queryKey);

  if (!queryMeta) {
    return current;
  }

  const nextItems = [...current.items];
  const existingIndex = nextItems.findIndex((item) => item.transaction_id === payload.transactionId);

  if (existingIndex >= 0) {
    const existingItem = nextItems[existingIndex];
    if (!existingItem) {
      return current;
    }

    nextItems[existingIndex] = mergeReceiptRow(existingItem, payload, candidateRow.branch_name);

    return {
      ...current,
      items: nextItems,
    };
  }

  if (!matchesBranchAndWindow(payload, queryMeta.branchId, queryMeta.period, queryMeta.from, queryMeta.to)) {
    return current;
  }

  const nextTotal = current.total + 1;

  if (queryMeta.page !== 1) {
    return {
      ...current,
      total: nextTotal,
    };
  }

  nextItems.unshift(candidateRow);
  if (nextItems.length > queryMeta.pageSize) {
    nextItems.pop();
  }

  return {
    ...current,
    total: nextTotal,
    items: nextItems,
  };
}

export function applyReceiptCreatedEventToDashboardSummary(args: {
  current: MerchantSummaryResponse;
  queryKey: QueryKey;
  payload: NormalizedReceiptCreatedEvent;
}): MerchantSummaryResponse {
  const { current, queryKey, payload } = args;
  const queryMeta = parseDashboardSummaryQueryKey(queryKey);

  if (!queryMeta) {
    return current;
  }

  if (
    !matchesBranchAndWindow(payload, queryMeta.branchId, queryMeta.period, queryMeta.from, queryMeta.to)
  ) {
    return current;
  }

  return {
    ...current,
    period_now: {
      ...current.period_now,
      receipts_count: current.period_now.receipts_count + 1,
      revenue_centavos:
        current.period_now.revenue_centavos === null
          ? null
          : current.period_now.revenue_centavos + (payload.totalCentavosNumber ?? 0),
    },
  };
}

function toCandidateReceiptRow(
  payload: NormalizedReceiptCreatedEvent,
  branchName: string | undefined,
): MerchantReceiptItem {
  return {
    branch_name: branchName ?? 'New receipt',
    captured_at: payload.capturedAt,
    has_image: payload.hasImage,
    items_count: 0,
    merchant_corrected_at: null,
    parse_confidence: null,
    status: payload.status,
    total_centavos: payload.totalCentavos,
    transaction_id: payload.transactionId,
  };
}

function mergeReceiptRow(
  previous: MerchantReceiptItem,
  payload: NormalizedReceiptCreatedEvent,
  fallbackBranchName: string,
): MerchantReceiptItem {
  return {
    ...previous,
    branch_name: previous.branch_name || fallbackBranchName,
    captured_at: payload.capturedAt,
    has_image: payload.hasImage,
    status: previous.status || payload.status,
    total_centavos: payload.totalCentavos ?? previous.total_centavos,
  };
}

function matchesReceiptFilters(
  payload: NormalizedReceiptCreatedEvent,
  queryMeta: ReceiptsQueryKeyMeta,
): boolean {
  if (!matchesBranchAndWindow(payload, queryMeta.branchId, queryMeta.period, queryMeta.from, queryMeta.to)) {
    return false;
  }

  if (queryMeta.status !== null && queryMeta.status !== payload.status) {
    return false;
  }

  if (queryMeta.hasImage !== null && queryMeta.hasImage !== payload.hasImage) {
    return false;
  }

  if (queryMeta.q !== null) {
    const normalizedQuery = queryMeta.q.trim().toLowerCase();
    if (!normalizedQuery) {
      return true;
    }

    return payload.transactionId.toLowerCase().includes(normalizedQuery);
  }

  return true;
}

function matchesBranchAndWindow(
  payload: NormalizedReceiptCreatedEvent,
  queryBranchId: string | null,
  period: MerchantSummaryPeriod | MerchantReceiptPeriod | null,
  from: string | null,
  to: string | null,
): boolean {
  if (queryBranchId !== null && queryBranchId !== payload.branchId) {
    return false;
  }

  const range = resolveQueryWindow(period, from, to, Date.now());
  if (!range) {
    return true;
  }

  const capturedAtMs = Date.parse(payload.capturedAt);
  return capturedAtMs >= range.fromMs && capturedAtMs <= range.toMs;
}

function resolveQueryWindow(
  period: MerchantSummaryPeriod | MerchantReceiptPeriod | null,
  from: string | null,
  to: string | null,
  nowMs: number,
): { fromMs: number; toMs: number } | null {
  if (from && to) {
    const fromMs = Date.parse(from);
    const toMs = Date.parse(to);

    if (!Number.isNaN(fromMs) && !Number.isNaN(toMs) && fromMs <= toMs) {
      return { fromMs, toMs };
    }

    return null;
  }

  if (!period) {
    return null;
  }

  let daySpan: number;

  switch (period) {
    case 'today':
      daySpan = 1;
      break;
    case 'yesterday':
      daySpan = 1;
      break;
    case '7d':
      daySpan = 7;
      break;
    case '30d':
      daySpan = 30;
      break;
    case '90d':
      daySpan = 90;
      break;
    case 'custom':
      return null;
    default:
      return null;
  }

  const todayManilaDate = parseManilaDateKey(toManilaDateKey(new Date(nowMs)));

  if (period === 'yesterday') {
    const yesterday = addDays(todayManilaDate, -1);
    return {
      fromMs: Date.parse(toManilaDayBoundaryIso(yesterday, 'start')),
      toMs: Date.parse(toManilaDayBoundaryIso(yesterday, 'end')),
    };
  }

  const startDate = addDays(todayManilaDate, -(daySpan - 1));

  return {
    fromMs: Date.parse(toManilaDayBoundaryIso(startDate, 'start')),
    toMs: Date.parse(toManilaDayBoundaryIso(todayManilaDate, 'end')),
  };
}

function parseReceiptsQueryKey(queryKey: QueryKey): ReceiptsQueryKeyMeta | null {
  if (
    !Array.isArray(queryKey) ||
    queryKey[0] !== RECEIPTS_QUERY_KEY_PREFIX[0] ||
    queryKey[1] !== RECEIPTS_QUERY_KEY_PREFIX[1]
  ) {
    return null;
  }

  const [
    ,
    ,
    q,
    status,
    hasImage,
    period,
    from,
    to,
    branchId,
    page,
    pageSize,
  ] = queryKey;

  return {
    q: typeof q === 'string' ? q : null,
    status: status === 'pending' || status === 'done' || status === 'failed' ? status : null,
    hasImage: typeof hasImage === 'boolean' ? hasImage : null,
    period: period === 'today' || period === '7d' || period === '30d' || period === '90d' ? period : null,
    from: typeof from === 'string' ? from : null,
    to: typeof to === 'string' ? to : null,
    branchId: typeof branchId === 'string' ? branchId : null,
    page: typeof page === 'number' && Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: typeof pageSize === 'number' && Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 20,
  };
}

function parseDashboardRecentReceiptsQueryKey(queryKey: QueryKey): DashboardRecentReceiptsQueryKeyMeta | null {
  if (
    !Array.isArray(queryKey) ||
    queryKey[0] !== DASHBOARD_RECENT_RECEIPTS_QUERY_KEY_PREFIX[0] ||
    queryKey[1] !== DASHBOARD_RECENT_RECEIPTS_QUERY_KEY_PREFIX[1]
  ) {
    return null;
  }

  const [, , period, from, to, branchId, page, pageSize] = queryKey;

  return {
    period:
      period === 'today' ||
      period === 'yesterday' ||
      period === '7d' ||
      period === '30d' ||
      period === 'custom'
        ? period
        : null,
    from: typeof from === 'string' ? from : null,
    to: typeof to === 'string' ? to : null,
    branchId: typeof branchId === 'string' ? branchId : null,
    page: typeof page === 'number' && Number.isInteger(page) && page > 0 ? page : 1,
    pageSize: typeof pageSize === 'number' && Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 5,
  };
}

function parseDashboardSummaryQueryKey(queryKey: QueryKey): DashboardSummaryQueryKeyMeta | null {
  if (
    !Array.isArray(queryKey) ||
    queryKey[0] !== DASHBOARD_SUMMARY_QUERY_KEY_PREFIX[0] ||
    queryKey[1] !== DASHBOARD_SUMMARY_QUERY_KEY_PREFIX[1]
  ) {
    return null;
  }

  const [, , period, from, to, branchId] = queryKey;

  if (
    period !== 'today' &&
    period !== 'yesterday' &&
    period !== '7d' &&
    period !== '30d' &&
    period !== 'custom'
  ) {
    return null;
  }

  return {
    period,
    from: typeof from === 'string' ? from : null,
    to: typeof to === 'string' ? to : null,
    branchId: typeof branchId === 'string' ? branchId : null,
  };
}

function normalizeTotalCentavos(value: number | string | null | undefined): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return null;
    }

    return String(Math.trunc(parsed));
  }

  return null;
}

function toManilaDateKey(value: Date): string {
  const parts = manilaDateFormatter.formatToParts(value);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  if (!year || !month || !day) {
    return '1970-01-01';
  }

  return `${year}-${month}-${day}`;
}

function parseManilaDateKey(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return new Date(1970, 0, 1);
  }

  return new Date(year, month - 1, day);
}

function addDays(value: Date, days: number): Date {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

// ---------------------------------------------------------------------------
// Merchant notification center — `notification.new` SSE event handling.
// ---------------------------------------------------------------------------

type MerchantNotificationListResponse =
  paths['/api/v1/merchant/notifications']['get']['responses']['200']['content']['application/json'];

type MerchantNotificationRow = MerchantNotificationListResponse['items'][number];

type UnreadCountResponse =
  paths['/api/v1/merchant/notifications/unread-count']['get']['responses']['200']['content']['application/json'];

const NOTIFICATION_SEVERITIES = ['info', 'success', 'warning', 'critical'] as const;

export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

/** How the toast should render — maps 1:1 onto a sonner call. */
export type NotificationToastVariant = 'default' | 'success' | 'warning' | 'error';

/** Where clicking a notification should take the merchant. */
export type NotificationDeepLink =
  | { kind: 'receipt'; transactionId: string }
  | { kind: 'device' }
  | { kind: 'deal'; dealId: string }
  | { kind: 'product'; productId: string };

/** The `notification.new` SSE frame, normalized for the UI. */
export interface NormalizedNotificationEvent {
  id: string;
  type: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  createdAt: string;
}

function normalizeNotificationSeverity(value: unknown): NotificationSeverity {
  return NOTIFICATION_SEVERITIES.includes(value as NotificationSeverity)
    ? (value as NotificationSeverity)
    : 'info';
}

export function parseNotificationNewEventPayload(
  data: string,
): NormalizedNotificationEvent | null {
  let parsed: unknown;

  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const payload = parsed as Record<string, unknown>;
  const id = typeof payload.id === 'string' ? payload.id.trim() : '';
  const title = typeof payload.title === 'string' ? payload.title : '';

  if (!id || !title) {
    return null;
  }

  const createdAt = typeof payload.created_at === 'string' ? payload.created_at : '';
  if (!createdAt || Number.isNaN(Date.parse(createdAt))) {
    return null;
  }

  return {
    id,
    type: typeof payload.type === 'string' ? payload.type : 'unknown',
    severity: normalizeNotificationSeverity(payload.severity),
    title,
    body: typeof payload.body === 'string' ? payload.body : null,
    data:
      payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
        ? (payload.data as Record<string, unknown>)
        : null,
    createdAt,
  };
}

export function resolveNotificationToastVariant(
  severity: NotificationSeverity,
): NotificationToastVariant {
  switch (severity) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'critical':
      return 'error';
    case 'info':
    default:
      return 'default';
  }
}

/**
 * Notification `type` values that carry a `deal_id` and should deep-link to the
 * deal detail page. The backend emits these merchant-scoped.
 */
const DEAL_DEEP_LINK_TYPES = new Set(['deal_approved', 'deal_rejected', 'deal_ending_soon']);

/** Notification `type` values that carry a `product_id` → product detail page. */
const PRODUCT_DEEP_LINK_TYPES = new Set(['product_needs_photo']);

/** Read a non-blank string field from a data bag, else empty string. */
function readTrimmedString(
  data: Record<string, unknown> | null | undefined,
  key: string,
): string {
  const value = data?.[key];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Resolve a notification's `type` + `data` bag into a typed in-app deep link.
 *
 * Receipt notifications carry both `receipt_id` and `transaction_id`; the
 * receipt detail route is keyed by the transaction id, so that wins. Device
 * notifications carry `device_id` — there is no per-device route today, so
 * they land on the devices list. Deal notifications (`deal_approved`,
 * `deal_rejected`, `deal_ending_soon`) carry `deal_id` → the deal detail
 * route; `product_needs_photo` carries `product_id` → the product detail
 * route. In every case a missing/blank id yields no link (the row simply
 * isn't clickable) rather than a broken navigation.
 */
export function getNotificationDeepLink(
  notification: { type?: string | null; data?: Record<string, unknown> | null },
): NotificationDeepLink | null {
  const type = typeof notification.type === 'string' ? notification.type : '';
  const data = notification.data ?? null;

  const transactionId = readTrimmedString(data, 'transaction_id');
  if (transactionId) {
    return { kind: 'receipt', transactionId };
  }

  if (DEAL_DEEP_LINK_TYPES.has(type)) {
    const dealId = readTrimmedString(data, 'deal_id');
    return dealId ? { kind: 'deal', dealId } : null;
  }

  if (PRODUCT_DEEP_LINK_TYPES.has(type)) {
    const productId = readTrimmedString(data, 'product_id');
    return productId ? { kind: 'product', productId } : null;
  }

  const deviceId = readTrimmedString(data, 'device_id');
  if (deviceId) {
    return { kind: 'device' };
  }

  return null;
}

function toNotificationRow(event: NormalizedNotificationEvent): MerchantNotificationRow {
  return {
    id: event.id,
    type: event.type,
    severity: event.severity,
    title: event.title,
    body: event.body,
    data: event.data,
    read_at: null,
    created_at: event.createdAt,
  };
}

export function applyNotificationNewEventToList(
  current: InfiniteData<MerchantNotificationListResponse> | undefined,
  event: NormalizedNotificationEvent,
): InfiniteData<MerchantNotificationListResponse> | undefined {
  if (!current || current.pages.length === 0) {
    return current;
  }

  const alreadyPresent = current.pages.some((page) =>
    page.items.some((item) => item.id === event.id),
  );
  if (alreadyPresent) {
    return current;
  }

  const [firstPage, ...restPages] = current.pages;
  if (!firstPage) {
    return current;
  }

  const patchedFirstPage: MerchantNotificationListResponse = {
    ...firstPage,
    items: [toNotificationRow(event), ...firstPage.items],
    unread_count: firstPage.unread_count + 1,
  };

  return {
    ...current,
    pages: [
      patchedFirstPage,
      ...restPages.map((page) => ({ ...page, unread_count: page.unread_count + 1 })),
    ],
  };
}

/**
 * Prepend the freshly-arrived notification into every inbox cache and bump the
 * unread-count cache, so the bell badge + center update instantly without a
 * refetch. Mirrors {@link patchReceiptCreatedEventInCache}.
 */
export function patchNotificationNewEventInCache(args: {
  queryClient: QueryClient;
  event: NormalizedNotificationEvent;
}) {
  const { queryClient, event } = args;

  const listQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: NOTIFICATIONS_LIST_QUERY_KEY_PREFIX });

  for (const query of listQueries) {
    queryClient.setQueryData<InfiniteData<MerchantNotificationListResponse>>(
      query.queryKey,
      (current) => applyNotificationNewEventToList(current, event),
    );
  }

  queryClient.setQueryData<UnreadCountResponse>(
    NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
    (current) => ({ unread_count: (current?.unread_count ?? 0) + 1 }),
  );
}
